import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { SessionHeader } from '../api/types/common';
import type { CurrentUser } from '../api/types/auth';
import { clearAllBookingDrafts } from '../booking-flow/draftStorage';

/**
 * Single source of truth for session + current user. Plain zustand (not a React
 * context) on purpose: api/client.ts reads useAuthStore.getState() outside any
 * component tree to inject sessionHeader into every request.
 */
export type AuthStatus = 'idle' | 'awaiting-otp' | 'authenticated';

export interface PendingOtp {
  /** The `sessionId` returned by `Login` that identifies the pending 2FA login. */
  sessionId: string;
  maskedEmail?: string;
  /** Carried through from the Login response so it survives the OTP step. */
  employeeId?: number;
}

interface AuthState {
  status: AuthStatus;
  session: SessionHeader | null;
  currentUser: CurrentUser | null;
  /** From the `Login` / `Session/Verify` response — the logged-in user's corporate
   *  employee id (0 for non-corporate). Used by the self-service "Firma Profilim" page. */
  employeeId: number | null;
  pendingOtp: PendingOtp | null;
  setSession: (session: SessionHeader, employeeId?: number) => void;
  setCurrentUser: (user: CurrentUser) => void;
  /** Login succeeded but a second factor is required — hold here until `Session/Verify`. */
  setAwaitingOtp: (pending: PendingOtp) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      status: 'idle',
      session: null,
      currentUser: null,
      employeeId: null,
      pendingOtp: null,
      setSession: (session, employeeId) =>
        set({
          session,
          status: 'authenticated',
          pendingOtp: null,
          ...(employeeId !== undefined ? { employeeId } : {}),
        }),
      setCurrentUser: (currentUser) => set({ currentUser }),
      setAwaitingOtp: (pendingOtp) => set({ pendingOtp, status: 'awaiting-otp', session: null }),
      logout: () => {
        // Booking drafts are user-scoped and only usable against the now-dead session —
        // don't let a second user on the same tab inherit them.
        clearAllBookingDrafts();
        set({ session: null, currentUser: null, employeeId: null, status: 'idle', pendingOtp: null });
      },
    }),
    {
      name: 'tfw-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        status: state.status,
        session: state.session,
        currentUser: state.currentUser,
        employeeId: state.employeeId,
        pendingOtp: state.pendingOtp,
      }),
    }
  )
);
